
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export const useAuthForms = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCheckingSubmission, setIsCheckingSubmission] = useState(false);
  const [quickSubmission, setQuickSubmission] = useState<any>(null);
  const [isResetMode, setIsResetMode] = useState(false);
  const quickSubmissionId = searchParams.get('quick_submission');

  // Check if user is already logged in
  useEffect(() => {
    if (user) {
      navigate('/job-seeker-dashboard');
    }
  }, [user, navigate]);

  // Check if there's a quick submission ID in the URL params
  useEffect(() => {
    const fetchQuickSubmission = async () => {
      if (quickSubmissionId) {
        setIsCheckingSubmission(true);
        try {
          const { data, error } = await supabase
            .from('quick_submissions')
            .select('*')
            .eq('id', quickSubmissionId)
            .single();

          if (error) {
            console.error('Error fetching quick submission:', error);
            toast.error('Could not find your submission information.');
          } else if (data) {
            setQuickSubmission(data);
            setEmail(data.email);
          }
        } catch (err) {
          console.error('Error checking quick submission:', err);
        } finally {
          setIsCheckingSubmission(false);
        }
      }
    };

    fetchQuickSubmission();
  }, [quickSubmissionId]);

  // Check if this is a password reset session
  useEffect(() => {
    const isReset = searchParams.get('reset') === 'true';
    
    if (isReset) {
      // This is a password reset session
      setIsResetMode(true);
    }
  }, [searchParams]);

  const sendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/job-seeker-dashboard`,
          data: {
            role: 'job_seeker',
            from_quick_submission: quickSubmissionId || null
          }
        }
      });
      
      if (error) {
        throw error;
      }
      
      toast.success('Magic link sent! Check your email to sign in.', {
        duration: 6000,
        description: 'Please check your email inbox and click on the link to sign in.'
      });
    } catch (error: any) {
      console.error('Magic link error:', error);
      toast.error('Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    loading,
    setLoading,
    isCheckingSubmission,
    quickSubmission,
    isResetMode,
    setIsResetMode,
    quickSubmissionId,
    sendMagicLink
  };
};
